import { useState, useEffect, useCallback } from 'react';
import { Megaphone, Plus, Pencil, Trash2, X, AlertTriangle, Info, Sparkles, Clock } from 'lucide-react';
import { Modal, Toggle, Spinner } from '@/components/shared/UIComponents';
import { fetchAnnouncements, createAnnouncement, updateAnnouncement, deleteAnnouncement } from '@/lib/api';
import type { Tenant, Announcement, AnnouncementType, CreateAnnouncementInput, UpdateAnnouncementInput } from '@/types';

const TYPE_OPTIONS: { value: AnnouncementType; label: string; icon: typeof Info; color: string; bg: string }[] = [
  { value: 'info',    label: 'Info',    icon: Info,           color: 'text-blue-400',  bg: 'banner-blue' },
  { value: 'warning', label: 'Warning', icon: AlertTriangle,  color: 'text-amber-400', bg: 'banner-amber' },
  { value: 'promo',   label: 'Promo',   icon: Sparkles,       color: 'text-teal-600',  bg: 'bg-teal-50 border-teal-200' },
];

function getTypeStyle(type: AnnouncementType) {
  return TYPE_OPTIONS.find(t => t.value === type) || TYPE_OPTIONS[0];
}

function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt).getTime() < Date.now();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Default expiry: 7 days from now in local datetime-local format
function defaultExpiry(): string {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 16);
}

export function AnnouncementsPage({ tenant }: { tenant: Tenant }) {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Announcement | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: '',
    content: '',
    type: 'info' as AnnouncementType,
    is_active: false,
    expires_at: defaultExpiry(),
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchAnnouncements(tenant.id);
      setAnnouncements(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, [tenant.id]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', content: '', type: 'info', is_active: false, expires_at: defaultExpiry() });
    setShowModal(true);
  };

  const openEdit = (a: Announcement) => {
    setEditing(a);
    setForm({
      title: a.title,
      content: a.content,
      type: a.type,
      is_active: a.is_active,
      expires_at: new Date(a.expires_at).toISOString().slice(0, 16),
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        const input: UpdateAnnouncementInput = {
          title: form.title,
          content: form.content,
          type: form.type,
          is_active: form.is_active,
          expires_at: new Date(form.expires_at).toISOString(),
        };
        const updated = await updateAnnouncement(editing.id, tenant.id, input);
        setAnnouncements(prev => prev.map(a => a.id === updated.id ? updated : (input.is_active ? { ...a, is_active: false } : a)));
      } else {
        const input: CreateAnnouncementInput = {
          title: form.title,
          content: form.content,
          type: form.type,
          is_active: form.is_active,
          expires_at: new Date(form.expires_at).toISOString(),
        };
        await createAnnouncement(tenant.id, input);
      }
      setShowModal(false);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAnnouncement(deleteTarget.id, tenant.id);
      setAnnouncements(prev => prev.filter(a => a.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (a: Announcement) => {
    try {
      const updated = await updateAnnouncement(a.id, tenant.id, { is_active: !a.is_active });
      // Reload to reflect deactivation of others
      if (updated.is_active) {
        await load();
      } else {
        setAnnouncements(prev => prev.map(x => x.id === a.id ? updated : x));
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  const activeCount = announcements.filter(a => a.is_active && !isExpired(a.expires_at)).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">         
          <div>
            <h1 className="text-xl font-bold text-surface-900">Announcements</h1>
            <p className="text-sm text-surface-500">
              Show announcements to your users on the login screen
            </p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Plus size={16} /> New Announcement
        </button>
      </div>

      {/* Active indicator */}
      {activeCount > 0 && (
        <div className="flex items-center gap-2 px-4 py-3 banner-emerald border rounded-xl">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm text-emerald-400 font-medium">
            1 announcement is live on your login screen
          </span>
        </div>
      )}

      {error && (
        <div className="px-4 py-3 banner-red border rounded-xl text-sm text-red-600 dark:text-red-400">
          {error}
          <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Announcements list */}
      {announcements.length === 0 ? (
        <div className="text-center py-16">
          <Megaphone className="w-12 h-12 text-surface-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-surface-700 mb-1">No announcements yet</h3>
          <p className="text-sm text-surface-500 mb-6">
            Create an announcement to display on your users' login screen.
          </p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus size={16} /> Create your first announcement
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {announcements.map(a => {
            const expired = isExpired(a.expires_at);
            const style = getTypeStyle(a.type);
            const TypeIcon = style.icon;

            return (
              <div
                key={a.id}
                className={`border rounded-xl p-5 transition-all ${
                  a.is_active && !expired
                    ? 'border-emerald-800/40 bg-emerald-900/10 shadow-sm'
                    : expired
                      ? 'border-surface-200 bg-surface-50 opacity-60'
                      : 'border-surface-200 bg-surface-0 hover:border-surface-300'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      {/* Type badge */}
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${style.bg} ${style.color}`}>
                        <TypeIcon size={12} />
                        {style.label}
                      </span>
                      {/* Active badge */}
                      {a.is_active && !expired && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-100 text-emerald-400 border border-emerald-800/40">
                          Live
                        </span>
                      )}
                      {/* Expired badge */}
                      {expired && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-surface-100 text-surface-500 border border-surface-200">
                          <Clock size={12} /> Expired
                        </span>
                      )}
                    </div>

                    <h3 className="font-semibold text-surface-900 mb-1">{a.title}</h3>
                    <p className="text-sm text-surface-600 line-clamp-2">{a.content}</p>

                    <div className="flex items-center gap-4 mt-3 text-xs text-surface-400">
                      <span>Created {formatDate(a.created_at)}</span>
                      <span>Expires {formatDateTime(a.expires_at)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {!expired && (
                      <Toggle
                        enabled={a.is_active}
                        onChange={() => handleToggleActive(a)}
                      />
                    )}
                    <button
                      onClick={() => openEdit(a)}
                      className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-700 transition-colors"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(a)}
                      className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-50 dark:bg-red-900/20 text-surface-400 hover:text-red-600 dark:text-red-400 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create / Edit Modal ────────────────────────────────── */}
      {showModal && (
        <Modal
          open={showModal}
          title={editing ? 'Edit Announcement' : 'New Announcement'}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Holiday hours update"
                maxLength={120}
                className="input-field text-sm"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Content</label>
              <textarea
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Write the announcement message your users will see..."
                rows={3}
                maxLength={500}
                className="input-field text-sm resize-none"
              />
              <div className="text-xs text-surface-400 text-right mt-1">{form.content.length}/500</div>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Type</label>
              <div className="flex gap-2">
                {TYPE_OPTIONS.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => setForm(f => ({ ...f, type: opt.value }))}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                        form.type === opt.value
                          ? `${opt.bg} ${opt.color} ring-2 ring-offset-1 ring-current`
                          : 'border-surface-200 text-surface-600 hover:border-surface-300'
                      }`}
                    >
                      <Icon size={14} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Expires at */}
            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Expires at</label>
              <input
                type="datetime-local"
                value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                min={new Date().toISOString().slice(0, 16)}
                className="input-field text-sm"
              />
              <p className="text-xs text-surface-400 mt-1">The announcement will automatically stop showing after this date.</p>
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-sm font-medium text-surface-700">Publish immediately</div>
                <div className="text-xs text-surface-400">Show this announcement on the login screen now</div>
              </div>
              <Toggle
                enabled={form.is_active}
                onChange={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2 border-t border-surface-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.title.trim() || !form.content.trim()}
                className="px-5 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirmation ────────────────────────────────── */}
      {deleteTarget && (
        <Modal
          open={!!deleteTarget}
          title="Delete Announcement"
          onClose={() => setDeleteTarget(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-surface-600">
              Are you sure you want to delete <strong>"{deleteTarget.title}"</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
